// Read-only content preview for sent/scheduled campaigns.

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typo, theme } from '../../../../ui';
import { CHANNEL, OFFER } from '../../constants';
import UpsellCardPreview from '../UpsellCardPreview';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;

const EmailShell = styled.div`
  max-width: 560px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 10px;
  padding: 24px;
`;

const Subject = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 6px;
`;

const Preview = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.NEU_600};
  margin-bottom: 18px;
`;

const Body = styled.div`
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  margin-bottom: 18px;
`;

const CtaButton = styled.a`
  display: inline-block;
  background: ${({ theme }) => theme.colors.PRIMARY};
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
`;

const OfferBadge = styled.div`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.SUCCESS_LIGHT};
  color: ${({ theme }) => theme.colors.SUCCESS_GREEN};
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 12px;
`;

const ContentTab = ({ campaign }) => {
  const { t } = useTranslation();
  const channels = campaign?.channels ?? [];
  const variant = campaign?.content?.variants?.[0];

  return (
    <>
      {campaign?.offer === OFFER.DISCOUNT && campaign?.discount?.code && (
        <Card>
          <OfferBadge>{campaign.discount.code} · {campaign.discount.percentage}% OFF</OfferBadge>
          <Typo.SmallText color='textSecondary'>
            {t('campaigns.content.discountHint', 'Discount code embedded into the email CTA and shown on the in-app card.')}
          </Typo.SmallText>
        </Card>
      )}

      {channels.includes(CHANNEL.EMAIL) && variant && (
        <Card>
          <Typo.TextTitle style={{ marginBottom: 18 }}>{t('campaigns.content.emailPreview', 'Email preview')}</Typo.TextTitle>
          <EmailShell>
            <Subject>{variant.subject || '—'}</Subject>
            <Preview>{variant.previewText || '—'}</Preview>
            <Body>{variant.body || '—'}</Body>
            {variant.callToAction?.url && (
              <CtaButton href={variant.callToAction.url} target='_blank' rel='noreferrer'>
                {variant.callToAction.text || t('campaigns.content.ctaDefault', 'Open')}
              </CtaButton>
            )}
          </EmailShell>
        </Card>
      )}

      {channels.includes(CHANNEL.NETWORK_UPSELL) && (
        <Card>
          <Typo.TextTitle style={{ marginBottom: 18 }}>{t('campaigns.content.inAppPreview', 'In-app card preview')}</Typo.TextTitle>
          <UpsellCardPreview campaign={campaign} theme={theme} />
        </Card>
      )}
    </>
  );
};

export default ContentTab;
